/**
 * Trial Service — client for the domain-trial endpoints.
 * Backend: RnkRocketBackend/src/routes/trial/index.ts
 */

import { apiClient } from './apiClient';

/**
 * The states `GET /trial/state` can return.
 *
 * 🔴 `awaiting_activation` was MISSING here while the backend was already
 * returning it (`routes/trial/index.ts`). The type omission was benign in the
 * three surfaces that gate on `state === 'active'` — they render nothing, which
 * is right — but `SubscriptionSection` derives its status pill from this union
 * by exhausting the cases it knows, and its pill is UNCONDITIONAL: an
 * unrecognised state fell through to `subscription.status` capitalised, so a
 * trialist whose clock had not started read **"Active"** next to a bare
 * "£19.95/month", with the "After trial · " prefix dropped and the trial callout
 * suppressed. For up to 72 hours, nothing anywhere told them they were on a
 * trial.
 *
 * What it MEANS (see the route's own comment): access is live, the 14-day clock
 * has NOT started, and `trialStartAt` / `trialEndAt` / `daysRemaining` therefore
 * all come back null. It must never be rendered as "0 days" and it must never
 * be rendered as an ordinary running trial — neither is true.
 */
export type TrialEffectiveState =
  | 'none'
  | 'active'
  | 'awaiting_activation'
  | 'expired_soft_locked'
  | 'paid'
  | 'released';

export interface TrialState {
  state: TrialEffectiveState;
  domainKey: string | null;
  trialStartAt: string | null;
  trialEndAt: string | null;
  daysRemaining: number | null;
  hasPaidAccess: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * GET /api/trial/state
 *
 * apiClient.get<ApiResponse<T>>() returns the raw backend JSON body,
 * which is already shaped as { success, data, message }.
 */
export async function fetchTrialState(): Promise<TrialState | null> {
  try {
    const response = await apiClient.get<ApiResponse<TrialState>>('trial/state', { cache: false });
    if (response && typeof response === 'object' && 'data' in response && response.data) {
      return response.data as TrialState;
    }
    return null;
  } catch (err) {
    // 401 / network / not-logged-in: treat as "no trial info".
    return null;
  }
}
